import { ConfigurationContext } from "Components/Generic/Configuration";
import {
  useCallback, useContext, useEffect, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { duplicateCheckActiveProjectItemIdSelect, duplicateCheckCacheSet } from "Store/Actions";
import { selectDuplicateCheckCache } from "Store/Selectors/Omikron";
import { getItemType, updateProjectItem } from "Utils/Item";
import { useProjectItem } from "../../Hooks/UseProjectItem";
import { ProjectItemContext } from "../../ProjectItemContext";
import { IUseMergedItem } from "./IUseMergedItem";
import { useResult } from "./UseResult";

export function useMergedItem(): IUseMergedItem {
  const dispatch = useDispatch();
  const { projectId, projectItemId, selectedResultItemId: resultItemId } = useContext(ProjectItemContext);
  const { project, projectItem, nextProjectItemId } = useProjectItem(projectId, projectItemId);
  const cache = useSelector(selectDuplicateCheckCache);
  const [mergedProjectItem, setMergedProjectItem] = useState(projectItem);
  const [mergeSelectionMap, setMergeSelectionMap] = useState(new Map<string, "source" | "target">());
  const [isMergeSaved, setIsMergeSaved] = useState(false);
  const result = useResult(resultItemId);

  useEffect(() => {
    setMergedProjectItem(projectItem);
    setMergeSelectionMap(new Map<string, "source" | "target">());
    setIsMergeSaved(false);
  }, [projectItem, resultItemId]);

  const onMergeChange = useCallback(
    (key: string, value: unknown) => {
      const [sourceOrTarget, fieldName] = key.split(`.`);
      if (mergeSelectionMap.get(fieldName) === sourceOrTarget) {
        return;
      }

      setMergeSelectionMap((previous) => new Map(previous.set(fieldName, sourceOrTarget as "source" | "target")));
      setMergedProjectItem((previous) => ({
        ...previous,
        [fieldName]: value ?? null
      }));
    },
    [mergeSelectionMap]
  );

  const config = useContext(ConfigurationContext);
  const fieldNameList = config.getDCMergeFields(getItemType(projectItem)).map(({ fieldName }) => fieldName);

  const onSelectAll = useCallback(
    (selectedKey: "source" | "target") => {
      setMergeSelectionMap((previous) => {
        const clone = new Map(previous);
        for (const fieldName of fieldNameList) {
          clone.set(fieldName, selectedKey);
        }
        return clone;
      });

      setMergedProjectItem((previous) => {
        const clone = { ...previous };
        if (selectedKey === `source`) {
          for (const fieldName of fieldNameList) {
            if (Object.hasOwn(clone, fieldName)) {
              delete clone[fieldName];
            }
          }
        } else if (selectedKey === `target`) {
          for (const fieldName of fieldNameList) {
            if (Object.hasOwn(result, fieldName)) {
              clone[fieldName] = result[fieldName];
            }
          }
        }

        return clone;
      });
    },
    [fieldNameList, result]
  );

  const onMergeSave = useCallback(
    (pItemId: string) => {
      if (mergedProjectItem == null) {
        return;
      }

      const updatedProject = updateProjectItem(cache ?? project, pItemId, () => ({
        ...mergedProjectItem,
        "@isDC": true,
        "@targetId": result[`@targetId`],
        "@targetUrl": result[`@targetUrl`]
      }));
      dispatch(duplicateCheckCacheSet(updatedProject));
      dispatch(duplicateCheckActiveProjectItemIdSelect(nextProjectItemId));
      setIsMergeSaved(true);
    },
    [cache, dispatch, mergedProjectItem, nextProjectItemId, project, result]
  );

  return {
    isMergeSaved,
    mergeSelectionMap,
    mergedProjectItem,
    onMergeChange,
    onMergeSave,
    onSelectAll
  };
}
